/**
 * 字符串类型筛选器默认值配置
 * - 支持包含 / 排除
 * - 支持全选
 * - 支持字段时间范围过滤
 */

import React, { useMemo, useEffect, useRef } from 'react';
import { Form } from 'tea-component';
import { useIntl } from 'umi';
import StringSelectorWithTag from '@/pages/datalab/components/StringSelectorWithTag';
import FieldTimeRange from '@/pages/datalab/components/StringSelectorWithTag/FieldTimeRange';
import {
  useStringSelectorProps,
  getDefaultStringOptionTimeFilter,
} from '@/pages/datalab/components/StringSelectorWithTag/hooks';
import { TIncludeOrExclude } from '@/pages/datalab/components/StringSelectorWithTag/type';
import { getReportDimensionInfo } from '@dbl/services/filter';
import type { IFilterState, IStringDefaultValue } from '../../types';

interface IFilterStringDefaultValueProps {
  state: IFilterState;
  dispatch: React.Dispatch<any>;
  gameCode: string;
  filterColumnRef: React.RefObject<{ getDateColumns: () => any[]; }>;
}

const FilterStringDefaultValue: React.FC<IFilterStringDefaultValueProps> = ({
  state,
  dispatch,
  gameCode,
  filterColumnRef,
}) => {
  const { formatMessage } = useIntl();

  const stringDefaultValue = state.defaultValue as IStringDefaultValue | undefined;

  // 获取日期字段列表（用于 FieldTimeRange）
  // 根据需求文档：当前数据集存在至少一个日期字段且筛选器类型为字符串时，显示"选项时间范围"控件
  // 直接从 ref 中读取，保证在日期字段准备好后可以立即反映到 UI 上
  const dateColumns = filterColumnRef.current?.getDateColumns?.() || [];

  /**
   * 构造字符串选项请求参数
   * - 参考旧版 Filter.tsx 中的 params 生成逻辑
   * - 包含默认的字段时间范围 filters
   * - 自定义字段时附带 custom_column / chart_id / expression（与旧版完全保持一致，是前后端约定好的固定模板）
   */
  const stringOptionParams = useMemo(() => {
    if (!state.dataset || !state.column) {
      return null;
    }

    // 默认时间范围过滤条件（参考旧版 Filter.tsx：优先使用第一个日期字段 + getDefaultStringOptionTimeFilter）
    let defaultFilter: any;
    const field = (dateColumns as any[])?.[0];
    if (field) {
      const defaultDateFilter = getDefaultStringOptionTimeFilter();
      defaultFilter = {
        display_name: field.display_name,
        display_name_en: field.display_name_en,
        column: field.name,
        column_type: field.type,
        table_id: field.table_id ?? state.dataset,
        // 自定义字段 SQL 表达式
        sql_expression: field.sql_expression,
        ...defaultDateFilter,
      };
    }

    // 旧数据中保存的字段时间范围过滤条件（defaultFieldFilters）
    const savedFilters = (stringDefaultValue as any)?.filters;
    const filters =
      Array.isArray(savedFilters) && savedFilters.length > 0
        ? savedFilters
        : defaultFilter
          ? [defaultFilter]
          : [];

    const params: any = {
      game_code: gameCode,
      table_id: state.dataset,
      dim_name: state.column,
      // 规则：v2 中不再区分业务类型，统一写死为 true
      dim_has_query_table: true,
      page_no: 1,
      page_size: 200,
      filters,
    };

    // 自定义字段请求参数兼容（严格沿用旧版 Filter.tsx 的固定模板，是前后端约定好的格式）
    if (state.columnInfo?.isCustomField && state.columnInfo?.sqlExpression) {
      params.custom_column = {
        custom_columns: [
          {
            name_cn: 'CustomField',
            column_type: 'float',
            name_en: 'CustomField',
            sql_expression: state.columnInfo.sqlExpression,
            accuracy: 2,
          },
        ],
        metrics: [
          {
            table_code: 't0',
            table_id: '',
            column: 'CustomField',
            label: 'CustomField',
            label_en: 'CustomField',
            column_type: 'string',
            aggregate: '',
            accuracy: 2,
            to_date_format: '',
          },
        ],
      };
      params.chart_id = state.columnInfo.chartId;
      // 与旧版 Filter.tsx 一致，expression 为 SQL 表达式字符串
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      params.expression = state.columnInfo.sqlExpression;
    }

    return params;
  }, [gameCode, state.dataset, state.column, state.columnInfo, dateColumns, stringDefaultValue]);

  // 字符串选择器相关逻辑
  // 使用 useStringSelectorProps hook 来管理字符串选项的请求和状态
  const {
    options,
    loading,
    error,
    getStringOptions,
    onSearchKeywordChange,
    isClearSearchWord,
  } = useStringSelectorProps({
    requestOptions: getReportDimensionInfo,
    params: stringOptionParams || undefined,
    manual: true,
  });

  // 记录已经为哪个「数据表 + 字段」做过一次自动请求，避免在无选项 / 接口报错时反复请求
  const autoRequestKeyRef = useRef<string | null>(null);

  // 首次渲染或字段变更时自动请求一次字符串选项列表
  // 目的：保证默认值下拉在初次展示时就有可选项，且每个字段只自动请求一次
  useEffect(() => {
    // 仅在字符串类型筛选器、已选择数据集和字段时触发
    if (
      state.filterType !== 'string' ||
      state.columnInfo?.isNumeric === true ||
      !state.dataset ||
      !state.column ||
      !stringOptionParams
    ) {
      return;
    }

    const key = `${state.dataset}__${state.column}`;
    if (autoRequestKeyRef.current === key) {
      // 当前数据表 + 字段已经做过一次自动请求，不再重复
      return;
    }
    autoRequestKeyRef.current = key;

    const filters = (stringDefaultValue as any)?.filters || stringOptionParams.filters || [];

    // 触发一次初始化请求，使用当前的字段时间范围 filters（如存在）
    getStringOptions({
      ...stringOptionParams,
      filters,
    });
  }, [
    state.filterType,
    state.dataset,
    state.column,
    state.columnInfo,
    stringOptionParams,
    stringDefaultValue,
    getStringOptions,
  ]);

  // 恢复 Time range 时优先使用旧数据中的 defaultFieldFilters[0]（已归一化为 stringDefaultValue.filters）
  // 这样可以完整还原字段元信息及时间范围配置，避免仅依赖简化的 fieldTimeRange 结构导致展示不一致
  const savedTimeFilter = useMemo(() => {
    const savedFilters = (stringDefaultValue as any)?.filters;
    if (Array.isArray(savedFilters) && savedFilters.length > 0) {
      return savedFilters[0];
    }
    return null;
  }, [stringDefaultValue]);

  // 处理字符串筛选器操作符变更
  const handleStringOperationChange = (operation: TIncludeOrExclude): void => {
    dispatch({
      type: 'SET_DEFAULT_VALUE',
      payload: {
        ...stringDefaultValue,
        operation: operation === 'include' ? 'include' : 'exclude',
      },
    });
  };

  // 处理字符串筛选器值变更
  const handleStringValueChange = (
    value: string[],
    isFull: boolean,
    customValues?: string[]
  ): void => {
    dispatch({
      type: 'SET_DEFAULT_VALUE',
      payload: {
        ...stringDefaultValue,
        value,
        isFull,
        customValues,
      },
    });
  };

  // 处理字段时间范围过滤变更
  // 上传的 filters 项必须包含日期字段的完整元信息（column、column_type、table_id、display_name 等），
  // 与旧版 Filter.tsx 的 defaultFieldFilter 结构一致，否则后端收到的会是空 column/column_type（错误上传内容）
  const handleFieldTimeRangeChange = (filter: any): void => {
    const { lastCount, dateType } = filter;
    const valueArr = lastCount === 'all_time' ? [] : [`${lastCount}-${dateType}`];
    const isFull = lastCount === 'all_time';

    // 解析当前选中的日期列：优先用 filter.column 或 filter.field 从 dateColumns 中匹配，否则用第一个日期列
    const dateCol = (dateColumns as any[]).find(
      (col: any) => (col.name || col.column) === (filter.column || filter.field)
    ) || (dateColumns as any[])?.[0];

    const oneFilter = dateCol
      ? {
        display_name: dateCol.display_name ?? '',
        display_name_en: dateCol.display_name_en ?? '',
        column: dateCol.name ?? dateCol.column ?? '',
        column_type: dateCol.type ?? dateCol.column_type ?? 'date',
        table_id: dateCol.table_id ?? state.dataset ?? '',
        table_code: dateCol.table_code ?? 't0',
        sql_expression: dateCol.sql_expression ?? '',
        operation: 'recent',
        lastCount,
        dateType: dateType || 'day',
        value: valueArr,
        is_full: isFull,
        last_day: filter.last_day ?? '-1',
        isCustomOption: filter.isCustomOption ?? false,
      }
      : {
        ...filter,
        value: valueArr,
        is_full: isFull,
      };

    const filters = [oneFilter];

    // 重新请求选项列表（基于统一的 stringOptionParams + 当前时间范围 filters）
    getStringOptions({
      ...(stringOptionParams || {}),
      filters,
    });

    // 更新字段时间范围配置
    dispatch({
      type: 'SET_DEFAULT_VALUE',
      payload: {
        ...stringDefaultValue,
        fieldTimeRange: {
          field: filter.column || filter.field || '',
          fieldType: filter.fieldType || 'date',
          timeRange: {
            type: lastCount === 'all_time' ? 'all' : 'recent',
            recentDays: lastCount === 'all_time' ? undefined : lastCount,
          },
        },
        filters,
      },
    });
  };
  
  const isNumericField = state.columnInfo?.isNumeric === true;

  return (
    <>
      {/* 字符串筛选器（非数值字段）时的选项值说明文案，等价于旧版 Filter.tsx 中的 option_value / filter_option_desc 提示 */}
      {!isNumericField && (
        <Form.Item
          label={formatMessage({ id: 'dbl_create_dashboard_option_value' })}
          showStatusIcon={false}
        >
          <span style={{ fontSize: 14 }}>
            {formatMessage({ id: 'dbl_create_dashboard_filter_option_desc' })}
          </span>
        </Form.Item>
      )}

      <Form.Item
        className="dbl-edit-filter-selector"
        label={formatMessage({ id: 'dbl_create_dashboard_default_value' })}
        showStatusIcon={false}
      >
        <StringSelectorWithTag
          placement="bottom-end"
          className="dbl-edit-string-selector"
          boxClassName="dbl-edit-filter-selector-string"
          name={state.columnInfo?.displayName || state.column || ''}
          options={state.column ? options : []}
          loading={loading}
          error={error as any}
          operation={(stringDefaultValue?.operation || 'include') as TIncludeOrExclude}
          onOperationChange={handleStringOperationChange}
          isClearSearchWord={isClearSearchWord}
          isAllState={stringDefaultValue?.isFull || false}
          isExclude={stringDefaultValue?.operation === 'exclude'}
          values={stringDefaultValue?.value || []}
          onCheckChange={(value, newIsFull: boolean, customValues) => {
            handleStringValueChange(value, newIsFull, customValues);
          }}
          // 下方属性为 StringSelectorWithTag 内部所需的运行时状态和回调，这里使用默认实现以满足类型约束
          total={options?.length || 0}
          searchState={false}
          onAllChange={() => {
            // 保持与 onCheckChange 行为一致：外部通过 isFull 控制“全选”状态
          }}
          listHeight={200}
          onSearchKeywordChange={onSearchKeywordChange}
          topContentRender={() => {
            // 字段选项时间范围过滤（FieldTimeRange）
            // 当存在日期字段时，显示时间范围过滤控件
            return dateColumns.length > 0 ? (
              <FieldTimeRange
                value={
                  (
                    // 1. 优先使用保存/恢复自旧数据的完整 filter（defaultFieldFilters[0]）
                    savedTimeFilter
                    // 2. 其次使用当前简化的 fieldTimeRange 结构组装一个部分值，保证新建场景也能工作
                    || (stringDefaultValue?.fieldTimeRange
                      ? {
                        field: stringDefaultValue.fieldTimeRange.field,
                        lastCount:
                            stringDefaultValue.fieldTimeRange.timeRange.type === 'all'
                              ? 'all_time'
                              : stringDefaultValue.fieldTimeRange.timeRange.recentDays || 7,
                        // 「全部时间」场景需要与 FieldTimeRange 预设值保持一致：dateType 为空字符串
                        dateType:
                            stringDefaultValue.fieldTimeRange.timeRange.type === 'all'
                              ? ''
                              : 'day',
                      }
                      // 3. 最后回退到默认配置（all time）
                      : getDefaultStringOptionTimeFilter())
                  ) as any
                }
                onChange={handleFieldTimeRangeChange}
                dateColumns={dateColumns}
              />
            ) : (
              ''
            );
          }}
        />
      </Form.Item>
    </>
  );
};

export default FilterStringDefaultValue;


